/**
 * BrandingPage Component
 *
 * The Branding tab: colour groups, logo slots, the publish bar, and a link that
 * opens the real Go client showing the draft.
 *
 * Deliberately does not reproduce any Go client view. The mockup's phone mock-ups
 * would drift against a client that changes often, and would be most wrong at the
 * moment someone relies on them to approve a brand.
 *
 * Requirements: 2.8, 4.6, 6.8, 11.3, 11.4, 11.6, 11.7, 11.8, 11.9
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getAppConfig } from '../../config/appConfig';
import { useAdvancedMode } from '../../hooks/useAdvancedMode';
import { useSite } from '../../site/context/useSite';
import { useBranding } from '../hooks/useBranding';
import {
  EDITABLE_ASSET_SLOTS,
  toColorsDraft,
  type BrandColorSlot,
} from '../models/domainModels';
import { COLOR_PAIRS } from '../utils/brandColors';
import { BrandLogoUpload } from './BrandLogoUpload';
import { ColorPairGroup } from './ColorPairGroup';
import { PreviewLink, type PreviewBay } from './PreviewLink';
import { PublishBar } from './PublishBar';
import './BrandingPage.css';

export function BrandingPage(): JSX.Element {
  const { siteId } = useParams<{ siteId: string }>();
  const { site } = useSite();
  const branding = useBranding(siteId);
  // Publishing is only offered behind `advanced=true` while the feature rolls out (Requirement 6.8).
  const { isAdvancedMode } = useAdvancedMode();

  const {
    published,
    draft,
    colors,
    hasUnsavedChanges,
    hasPendingDraft,
    contrastFindings,
    isLoading,
    isSaving,
    isPublishing,
    isMutating,
    canSave,
    error,
    authError,
    assetError,
    setColor,
    save,
    publish,
    reset,
    uploadAsset,
    removeAsset,
  } = branding;

  const workingBrand = draft ?? published;

  /** Slots differing from what is stored, for the per-field unsaved marker. */
  const changedSlots = useMemo(() => {
    const stored = workingBrand ? toColorsDraft(workingBrand.colors) : null;
    const changed = new Set<BrandColorSlot>();
    (Object.keys(colors) as BrandColorSlot[]).forEach((slot) => {
      if (colors[slot] !== (stored?.[slot] ?? '')) changed.add(slot);
    });
    return changed;
  }, [colors, workingBrand]);

  const findingByPair = useMemo(
    () => new Map(contrastFindings.map((finding) => [finding.pair, finding])),
    [contrastFindings]
  );

  /** Fixed bays have no name of their own, so they are labelled by id. */
  const bays: readonly PreviewBay[] = useMemo(() => {
    if (!site) return [];
    return [
      ...site.bays.fixedBays.map((bay) => ({ id: bay.id, name: `Bay ${bay.id}` })),
      ...site.bays.superBays.map((bay) => ({ id: bay.id, name: bay.name })),
    ];
  }, [site]);

  if (isLoading) {
    return (
      <div className="branding-page">
        <p className="branding-page__loading">Loading branding…</p>
      </div>
    );
  }

  if (authError === 'forbidden') {
    return (
      <div className="branding-page">
        <div className="branding-page__auth" role="alert">
          <h1 className="branding-page__title">Site Branding</h1>
          <p>
            Your account does not have permission to manage branding. Signing in again
            will not change this — ask the Toptracer Go team for the required access.
          </p>
        </div>
      </div>
    );
  }

  if (authError === 'expired') {
    return (
      <div className="branding-page">
        <div className="branding-page__auth" role="alert">
          <h1 className="branding-page__title">Site Branding</h1>
          <p>Your session has expired. Sign in again to continue.</p>
        </div>
      </div>
    );
  }

  /*
   * An incomplete colour is not flagged on its own field — that would put an error
   * under every field the moment it is typed into — so the reason saving is
   * unavailable has to surface here instead.
   */
  // Gated on `!isMutating` because `canSave` is also false while another action is
  // writing, and blaming that on the colour values would be a lie.
  const saveDisabledReason =
    !canSave && !isMutating
      ? 'Finish or clear the incomplete colour values before saving.'
      : null;

  /** Shown whole when it could not be attributed to individual slots. */
  const unattributedError =
    error !== null && error.slotMessages.size === 0 ? error.message : null;

  return (
    <div className="branding-page">
      <header className="branding-page__header">
        <h1 className="branding-page__title">Site Branding</h1>
        <PublishBar
          hasPendingDraft={hasPendingDraft}
          hasUnsavedChanges={hasUnsavedChanges}
          updatedAt={workingBrand?.updatedAt ?? null}
          canSave={canSave}
          isSaving={isSaving}
          isPublishing={isPublishing}
          isMutating={isMutating}
          saveDisabledReason={saveDisabledReason}
          showPublish={isAdvancedMode}
          onSave={save}
          onPublish={publish}
          onReset={reset}
        />
      </header>

      {unattributedError !== null && (
        <p className="branding-page__error" role="alert">
          {unattributedError}
        </p>
      )}

      {assetError !== null && (
        <p className="branding-page__error" role="alert">
          {assetError}
        </p>
      )}

      <div className="branding-page__columns">
        <section className="branding-page__panel">
          <h2 className="branding-page__panel-title">Branding Colours</h2>
          <p className="branding-page__panel-hint">
            Leave a colour empty to keep the Toptracer default for that slot.
          </p>

          {COLOR_PAIRS.map((pair) => (
            <ColorPairGroup
              key={pair.background}
              pair={pair}
              colors={colors}
              finding={findingByPair.get(`${pair.background}/${pair.foreground}`)}
              changedSlots={changedSlots}
              slotMessages={error?.slotMessages ?? new Map()}
              onChange={setColor}
            />
          ))}
        </section>

        <section className="branding-page__panel">
          <h2 className="branding-page__panel-title">Logos</h2>

          {EDITABLE_ASSET_SLOTS.map((slot) => (
            <BrandLogoUpload
              key={slot}
              slot={slot}
              asset={workingBrand?.assets[slot] ?? null}
              onUpload={(file, alt) => void uploadAsset(slot, file, alt)}
              onRemove={() => void removeAsset(slot)}
            />
          ))}
        </section>

        <div className="branding-page__aside">
          <PreviewLink
            appHost={getAppConfig().appHost}
            siteId={siteId ?? ''}
            hasBrand={workingBrand !== null}
            hasUnsavedChanges={hasUnsavedChanges}
            bays={bays}
          />
        </div>
      </div>
    </div>
  );
}
